import type { InvoiceDetailResponse, InvoicePart, InvoiceType, NexDetailLine } from '../../../types/invoice';
import { buildPartIndexRemapLayout, remapStoredPartIndex } from './invoiceDetailFormPartIndexRemap';
import type { LineItemField } from './invoiceDetailFormTypes';

export interface PreservedReconciliationSnapshot {
    matchedNexLines: Array<{ partIndex: number; nexLine: NexDetailLine }>;
    reconciliationMatchNotes: Array<{
        partIndex: number;
        invoicePartNumber: string;
        nexPartNumber: string;
        reason: string;
    }>;
}

export function buildCurrentReconciliationIssuesForSave(args: {
    invoice: InvoiceDetailResponse | undefined;
    editedFields: Partial<InvoiceDetailResponse>;
    lineItemBackendIndexByCurrentIndex: (number | undefined)[];
    preservedReconciliation: PreservedReconciliationSnapshot;
}): InvoiceDetailResponse['reconciliationIssues'] | undefined {
    const { invoice, editedFields, lineItemBackendIndexByCurrentIndex, preservedReconciliation } = args;
    const currentIssues = invoice?.reconciliationIssues;
    if (!currentIssues) return undefined;

    const layout = buildPartIndexRemapLayout(invoice, editedFields, lineItemBackendIndexByCurrentIndex);
    const remapEntryPartIndex = <T extends { partIndex: number }>(entry: T): T | null => {
        const nextIndex = remapStoredPartIndex(layout, entry.partIndex);
        if (nextIndex == null || (nextIndex < 0 && entry.partIndex >= 0)) return null;
        return { ...entry, partIndex: nextIndex };
    };

    const baseMatched =
        (invoice?.reconciliationIssues?.matchedNexLines?.length ?? 0) > 0
            ? invoice!.reconciliationIssues!.matchedNexLines!
            : preservedReconciliation.matchedNexLines;

    const remappedMatched = baseMatched
        .map((entry) => remapEntryPartIndex(entry))
        .filter((entry): entry is NonNullable<typeof entry> => entry != null);

    const baseNotes =
        ((invoice?.reconciliationIssues as { reconciliationMatchNotes?: PreservedReconciliationSnapshot['reconciliationMatchNotes'] } | undefined)
            ?.reconciliationMatchNotes?.length ?? 0) > 0
            ? ((invoice?.reconciliationIssues as { reconciliationMatchNotes?: PreservedReconciliationSnapshot['reconciliationMatchNotes'] })
                  .reconciliationMatchNotes ?? [])
            : preservedReconciliation.reconciliationMatchNotes;

    const remappedNotes = baseNotes
        .map((entry) => remapEntryPartIndex(entry))
        .filter((entry): entry is NonNullable<typeof entry> => entry != null);

    const remappedFlaggedParts = (currentIssues.flaggedParts ?? [])
        .map((entry) => remapEntryPartIndex(entry))
        .filter((entry): entry is NonNullable<typeof entry> => entry != null);

    const remappedProbableMatches = (currentIssues.probableMatches ?? [])
        .map((entry) => remapEntryPartIndex(entry))
        .filter((entry): entry is NonNullable<typeof entry> => entry != null);

    const probableMatchWithoutPartNumber = currentIssues.probableMatchWithoutPartNumber?.partIndices
        ? {
              partIndices: currentIssues.probableMatchWithoutPartNumber.partIndices
                  .map((idx) => remapStoredPartIndex(layout, idx))
                  .filter((idx): idx is number => idx != null),
          }
        : undefined;

    const editedMatchedIgnored = editedFields.reconciliationIssues?.matchedNexLinesForIgnored;

    return {
        ...currentIssues,
        ...(editedMatchedIgnored != null ? { matchedNexLinesForIgnored: editedMatchedIgnored } : {}),
        flaggedParts: remappedFlaggedParts,
        ...(remappedMatched.length > 0 ? { matchedNexLines: remappedMatched } : { matchedNexLines: undefined }),
        ...(remappedNotes.length > 0 ? { reconciliationMatchNotes: remappedNotes } : { reconciliationMatchNotes: undefined }),
        ...(remappedProbableMatches.length > 0 ? { probableMatches: remappedProbableMatches } : { probableMatches: undefined }),
        ...(probableMatchWithoutPartNumber && probableMatchWithoutPartNumber.partIndices.length > 0
            ? { probableMatchWithoutPartNumber }
            : { probableMatchWithoutPartNumber: undefined }),
    };
}

export function getResetPayloadFromOriginal(args: {
    invoice: InvoiceDetailResponse | undefined;
    orig: InvoiceDetailResponse['originalExtracted'];
    originalInvoiceType: InvoiceType | undefined;
}): Partial<InvoiceDetailResponse> {
    const { invoice, orig, originalInvoiceType } = args;
    if (!invoice || !orig) return {};
    const map = (arr: InvoicePart[] | undefined) => (arr ?? []).map((p) => ({ ...p }));
    const snapLocId = (orig as { locationId?: string }).locationId;
    const snapLocTitle = (orig as { locationTitle?: string }).locationTitle;
    return {
        invoiceNumber: orig.invoiceNumber,
        roNumber: orig.roNumber,
        ...(orig.purchaseOrderNumber != null ? { purchaseOrderNumber: orig.purchaseOrderNumber } : {}),
        ...(orig.shopRoNumber != null ? { shopRoNumber: orig.shopRoNumber } : {}),
        timestamp: orig.timestamp instanceof Date ? orig.timestamp : new Date(orig.timestamp),
        supplierText: orig.supplierText,
        invoiceType: originalInvoiceType ?? invoice.invoiceType ?? 'received',
        paySettlementType: orig.paySettlementType === 'charge' ? 'charge' : 'cash',
        ...(snapLocId != null && String(snapLocId).trim() !== ''
            ? {
                  locationId: String(snapLocId).trim(),
                  ...(snapLocTitle != null && String(snapLocTitle).trim() !== ''
                      ? { locationTitle: String(snapLocTitle).trim() }
                      : {}),
              }
            : {}),
        lineItems: map(orig.lineItems),
        freightItems: map(orig.freightItems),
        fuelItems: map(orig.fuelItems),
        restockingFeeItems: map(orig.restockingFeeItems),
        ignoredParts: map(orig.ignoredParts),
        discountItems: map(orig.discountItems ?? []),
        ...(orig.discountTotal !== undefined ? { discountTotal: orig.discountTotal } : {}),
        orderTotal: orig.orderTotal,
        subtotal: orig.subtotal,
        freightTotal: orig.freightTotal,
        taxesTotal: orig.taxesTotal,
        fuelTotal: orig.fuelTotal,
        restockingFee: orig.restockingFee,
        manualMatches: [],
        unmatchedPartIndices: [],
    };
}

export function getInvoiceTypeChangeResetPayload(
    invoice: InvoiceDetailResponse | undefined,
    nextType: InvoiceType,
): Partial<InvoiceDetailResponse> {
    const o = invoice?.originalExtracted;
    if (!invoice || !o) return {};
    const map = (arr: InvoicePart[] | undefined) => (arr ?? []).map((p) => ({ ...p }));
    return {
        invoiceNumber: o.invoiceNumber,
        roNumber: o.roNumber,
        ...(o.purchaseOrderNumber != null ? { purchaseOrderNumber: o.purchaseOrderNumber } : {}),
        ...(o.shopRoNumber != null ? { shopRoNumber: o.shopRoNumber } : {}),
        timestamp: o.timestamp instanceof Date ? o.timestamp : new Date(o.timestamp),
        supplierText: o.supplierText,
        invoiceType: nextType,
        paySettlementType: o.paySettlementType === 'charge' ? 'charge' : 'cash',
        ...(nextType === 'received' || nextType === 'sublet'
            ? {
                  ...(invoice.ccVendorId != null ? { ccVendorId: invoice.ccVendorId } : { ccVendorId: null }),
                  ...(invoice.ccVendorName != null
                      ? { ccVendorName: invoice.ccVendorName }
                      : { ccVendorName: null }),
              }
            : { ccVendorId: null, ccVendorName: null }),
        lineItems: map(o.lineItems),
        freightItems: map(o.freightItems),
        fuelItems: map(o.fuelItems),
        restockingFeeItems: map(o.restockingFeeItems),
        ignoredParts: map(o.ignoredParts),
        discountItems: map(o.discountItems ?? []),
        ...(o.discountTotal !== undefined ? { discountTotal: o.discountTotal } : {}),
        orderTotal: o.orderTotal,
        subtotal: o.subtotal,
        freightTotal: o.freightTotal,
        taxesTotal: o.taxesTotal,
        fuelTotal: o.fuelTotal,
        restockingFee: o.restockingFee,
        manualMatches: [],
        unmatchedPartIndices: [],
    };
}

export function getInvoiceTypeChangeFallbackPayload(
    invoice: InvoiceDetailResponse | undefined,
    nextType: InvoiceType,
): Partial<InvoiceDetailResponse> {
    if (!invoice) return {};
    const map = (arr: InvoicePart[] | undefined) => (arr ?? []).map((p) => ({ ...p }));
    return {
        invoiceNumber: invoice.invoiceNumber,
        roNumber: invoice.roNumber,
        ...(invoice.purchaseOrderNumber != null && invoice.purchaseOrderNumber !== ''
            ? { purchaseOrderNumber: invoice.purchaseOrderNumber }
            : {}),
        ...(invoice.shopRoNumber != null && invoice.shopRoNumber !== ''
            ? { shopRoNumber: invoice.shopRoNumber }
            : {}),
        timestamp: invoice.timestamp instanceof Date ? invoice.timestamp : new Date(invoice.timestamp),
        supplierText: invoice.supplierText,
        invoiceType: nextType,
        paySettlementType: invoice.paySettlementType === 'charge' ? 'charge' : 'cash',
        ...(nextType === 'received' || nextType === 'sublet'
            ? {
                  ...(invoice.ccVendorId != null ? { ccVendorId: invoice.ccVendorId } : { ccVendorId: null }),
                  ...(invoice.ccVendorName != null
                      ? { ccVendorName: invoice.ccVendorName }
                      : { ccVendorName: null }),
              }
            : { ccVendorId: null, ccVendorName: null }),
        lineItems: map(invoice.lineItems),
        freightItems: map(invoice.freightItems),
        fuelItems: map(invoice.fuelItems),
        restockingFeeItems: map(invoice.restockingFeeItems),
        ignoredParts: map(invoice.ignoredParts),
        discountItems: map(invoice.discountItems ?? []),
        ...(invoice.discountTotal !== undefined ? { discountTotal: invoice.discountTotal } : {}),
        orderTotal: invoice.orderTotal,
        subtotal: invoice.subtotal,
        freightTotal: invoice.freightTotal,
        taxesTotal: invoice.taxesTotal,
        fuelTotal: invoice.fuelTotal,
        restockingFee: invoice.restockingFee,
        manualMatches: [],
        unmatchedPartIndices: [],
    };
}

export const lineItemEditableFields: LineItemField[] = ['title', 'partNumber', 'qty', 'listPrice', 'unitBuyPrice', 'totalBuyPrice'];

export const lineItemFieldLabelMap: Record<string, string> = {
    title: 'Description',
    partNumber: 'Part number',
    qty: 'Quantity',
    listPrice: 'List Price',
    unitBuyPrice: 'Buy Price',
    totalBuyPrice: 'Total',
};
